import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
// v1.20 - Added transparent and white navigation variants

function Navigation({
  scrolled: scrolledProp,
  variant = 'default'
}) {
  const location = useLocation();
  const currentPath = (location?.pathname || '').replace(/[^a-zA-Z0-9]/g, '_') || 'root';
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [servicesDropdownOpen, setServicesDropdownOpen] = useState(false);
  const [aboutDropdownOpen, setAboutDropdownOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  // Handle scroll detection for transparent variant
  React.useEffect(() => {
    if (variant === 'transparent' || variant === 'transparent-to-solid') {
      const handleScroll = () => {
        setIsScrolled(window.scrollY > 50);
      };
      window.addEventListener('scroll', handleScroll);
      return () => window.removeEventListener('scroll', handleScroll);
    }
  }, [variant]);
  const scrolled = variant === 'transparent' || variant === 'transparent-to-solid' ? isScrolled : scrolledProp;

  // Determine navigation style based on variant
  const isDark = variant === 'dark' || !scrolled && variant === 'default';
  const isSoftBrown = variant === 'soft-brown';
  const isTransparentToSolid = variant === 'transparent-to-solid';
  const isTransparent = variant === 'transparent';
  const isWhite = variant === 'white';
  const navLinks = [{
    name: 'Home',
    href: '/',
    isRoute: true
  }, {
    name: 'About',
    href: '/my-approach',
    isRoute: true,
    hasDropdown: true,
    dropdownType: 'about'
  }, {
    name: 'Services',
    href: '#services',
    hasDropdown: true,
    dropdownType: 'services'
  }, {
    name: 'Blog',
    href: '/blog',
    isRoute: true
  }, {
    name: 'Testimonials',
    href: '/testimonials',
    isRoute: true
  }, {
    name: 'FAQ',
    href: '/faq',
    isRoute: true
  }, {
    name: 'Contact',
    href: '/contact',
    isRoute: true
  }];
  const aboutPages = [{
    name: 'My Approach',
    path: '/my-approach'
  }, {
    name: 'Our Team',
    path: '/about-me'
  }];
  const servicePages = [{
    name: 'Individual Therapy',
    path: '/services/individual-sessions'
  }, {
    name: 'Group Coaching',
    path: '/services/group-coaching'
  }];
  // Determine navigation background and text colors (reverted transparency changes)
  const getNavClasses = () => {
    return 'bg-white shadow-sm';
  };
  const getTextClasses = () => {
    return 'text-warm-700 hover:text-warm-600';
  };
  const getLogoClasses = () => {
    return 'text-warm-700';
  };
  return <nav className={`${getNavClasses()} transition-all duration-300 fixed top-12 left-0 right-0 z-40`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center py-4 md:py-6">
          {/* Logo */}
          <Link to="/" className={`text-xl md:text-2xl font-serif font-semibold transition-colors duration-300 ${getLogoClasses()}`} data-tracking-id={`link-f401d7c9f204ce38_${currentPath}`}><span>
            HopefulPaths
          </span></Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            {navLinks.map(link => link.hasDropdown ? <div key={link.name} className="relative group" onMouseLeave={() => link.dropdownType === 'services' ? setServicesDropdownOpen(false) : setAboutDropdownOpen(false)}>
                  <button className={`text-sm font-medium transition-colors duration-300 flex items-center gap-1 cursor-pointer ${getTextClasses()}`} onMouseEnter={() => link.dropdownType === 'services' ? setServicesDropdownOpen(true) : setAboutDropdownOpen(true)} data-tracking-id={`btn-75d9b517ee022e67_${link.name}`}>
                    {link.name}
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  
                  {/* About Dropdown */}
                  {link.dropdownType === 'about' && aboutDropdownOpen && <div className="absolute top-full left-0 pt-2 w-48 z-[100]" onMouseEnter={() => setAboutDropdownOpen(true)} onMouseLeave={() => setAboutDropdownOpen(false)}>
                      <div className="bg-gradient-to-br from-warm-50 to-sage-50 rounded-lg shadow-2xl py-3 border-2 border-warm-200" style={{
                backgroundColor: '#FFF5F0'
              }}>
                        {aboutPages.map(page => <Link key={page.path} to={page.path} className="block px-4 py-3 text-sm hover:bg-warm-200 hover:text-warm-800 transition-all duration-200 rounded-md mx-2 font-medium text-sage-800" onClick={() => setAboutDropdownOpen(false)} data-tracking-id={`link-f20c585eb5868b7d_${page.path}`}>
                            {page.name}
                          </Link>)}
                      </div>
                    </div>}
                  
                  {/* Services Dropdown */}
                  {link.dropdownType === 'services' && servicesDropdownOpen && <div className="absolute top-full left-0 pt-2 w-64 z-[100]" onMouseEnter={() => setServicesDropdownOpen(true)} onMouseLeave={() => setServicesDropdownOpen(false)}>
                      <div className="bg-gradient-to-br from-warm-50 to-sage-50 rounded-lg shadow-2xl py-3 border-2 border-warm-200" style={{
                backgroundColor: '#FFF5F0'
              }}>
                        {servicePages.map(service => service.isExternal ? <a key={service.path} href={service.path} className={`block px-4 py-3 text-sm hover:bg-warm-200 hover:text-warm-800 transition-all duration-200 rounded-md mx-2 font-medium ${service.featured ? 'bg-warm-100 text-warm-800 border-l-4 border-warm-500 font-semibold' : service.isFreeItem ? 'text-warm-600 border-t-2 border-warm-200 mt-2 pt-4 flex items-center justify-between' : 'text-sage-800'}`} onClick={() => setServicesDropdownOpen(false)} data-tracking-id={`link-e60c053182f0f6b1_${service.path}`}>
                            <span>{service.name}</span>
                            {service.isFreeItem && <span className="text-xs bg-warm-500 text-white px-2 py-1 rounded-full">
                                FREE
                              </span>}
                          </a> : <Link key={service.path} to={service.path} className={`block px-4 py-3 text-sm hover:bg-warm-200 hover:text-warm-800 transition-all duration-200 rounded-md mx-2 font-medium ${service.featured ? 'bg-warm-100 text-warm-800 border-l-4 border-warm-500 font-semibold' : service.isFreeItem ? 'text-warm-600 border-t-2 border-warm-200 mt-2 pt-4 flex items-center justify-between' : 'text-sage-800'}`} onClick={() => setServicesDropdownOpen(false)} data-tracking-id={`link-e60c053182f0f6b1_${service.path}`}>
                            <span>{service.name}</span>
                            {service.isFreeItem && <span className="text-xs bg-warm-500 text-white px-2 py-1 rounded-full">
                                FREE
                              </span>}
                          </Link>)}
                      </div>
                    </div>}
                </div> : link.isRoute ? <Link key={link.name} to={link.href} className={`text-sm font-medium transition-colors duration-300 ${getTextClasses()}`} data-tracking-id={`link-1451974d52a8e129_${link.name}`}>
                  {link.name}
                </Link> : <a key={link.name} href={link.href} className={`text-sm font-medium transition-colors duration-300 ${getTextClasses()}`} data-tracking-id={`link-b970f2339983f6af_${link.name}`}>
                  {link.name}
                </a>)}
          </div>

          {/* Mobile Menu Button */}
          <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className={`md:hidden p-2 rounded-lg transition-colors ${isTransparent && scrolled ? 'text-warm-700' : isTransparent && !scrolled ? 'text-white text-shadow-strong' : isWhite ? 'text-warm-700' : isDark ? 'text-white' : isTransparentToSolid && !scrolled ? 'text-white text-shadow-strong' : 'text-gray-700'}`} aria-label="Toggle menu" data-tracking-id={`btn-130401c0e4b2ad7e_${currentPath}`}>
            {mobileMenuOpen ? <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg> : <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && <div className={`md:hidden shadow-lg ${variant === 'dark' ? 'bg-sage-800 border-t border-sage-700' : 'bg-white border-t border-gray-200'}`}>
          <div className="px-4 py-4 space-y-3">
            {navLinks.map(link => link.hasDropdown ? <div key={link.name}>
                  <div className={`block py-2 font-medium ${isDark ? 'text-white/90' : 'text-gray-700'}`}>
                    {link.name}
                  </div>
                  <div className="ml-4 mt-2 space-y-2">
                    {link.dropdownType === 'about' && aboutPages.map(page => <Link key={page.path} to={page.path} onClick={() => setMobileMenuOpen(false)} className={`block py-2 text-sm hover:text-warm-500 transition-colors ${isDark ? 'text-white/80' : 'text-gray-600'}`} data-tracking-id={`link-4975dff5c78788c3_${page.path}`}>
                        {page.name}
                      </Link>)}
                    {link.dropdownType === 'services' && servicePages.map(service => service.isExternal ? <a key={service.path} href={service.path} onClick={() => setMobileMenuOpen(false)} className={`block py-2 text-sm hover:text-warm-500 transition-colors ${service.featured ? 'text-warm-600 font-semibold' : service.isFreeItem ? 'text-warm-600 font-semibold flex items-center justify-between border-t border-warm-200 pt-3 mt-2' : isDark ? 'text-white/80' : 'text-gray-600'}`} data-tracking-id={`link-bd37424a8d357b2d_${service.path}`}>
                        <span>{service.name}</span>
                        {service.isFreeItem && <span className="text-xs bg-warm-500 text-white px-2 py-0.5 rounded-full">
                            FREE
                          </span>}
                      </a> : <Link key={service.path} to={service.path} onClick={() => setMobileMenuOpen(false)} className={`block py-2 text-sm hover:text-warm-500 transition-colors ${service.featured ? 'text-warm-600 font-semibold' : service.isFreeItem ? 'text-warm-600 font-semibold flex items-center justify-between border-t border-warm-200 pt-3 mt-2' : isDark ? 'text-white/80' : 'text-gray-600'}`} data-tracking-id={`link-bd37424a8d357b2d_${service.path}`}>
                        <span>{service.name}</span>
                        {service.isFreeItem && <span className="text-xs bg-warm-500 text-white px-2 py-0.5 rounded-full">
                            FREE
                          </span>}
                      </Link>)}
                  </div>
                </div> : link.isRoute ? <Link key={link.name} to={link.href} onClick={() => setMobileMenuOpen(false)} className={`block py-2 hover:text-warm-500 transition-colors font-medium ${isDark ? 'text-white/90' : 'text-gray-700'}`} data-tracking-id={`link-72f66f63f70f0100_${link.name}`}>
                  {link.name}
                </Link> : <a key={link.name} href={link.href} onClick={() => setMobileMenuOpen(false)} className={`block py-2 hover:text-warm-500 transition-colors font-medium ${isDark ? 'text-white/90' : 'text-gray-700'}`} data-tracking-id={`link-a2251efa3b702b45_${link.name}`}>
                  {link.name}
                </a>)}
            <a href="https://calendly.com/yphilipos/30min" target="_blank" rel="noopener noreferrer" onClick={() => setMobileMenuOpen(false)} className="block text-center px-6 py-3 bg-warm-500 text-white rounded-full font-medium hover:bg-warm-600 transition-colors" data-tracking-id={`link-f64e7c087b29350c_${currentPath}`}>
              Book Consultation
            </a>
          </div>
        </div>}
    </nav>;
}
export default Navigation;